import { Injectable } from '@angular/core'
import { type LiveEditingContentInitEvent, type LiveEditingStatusEvent, LiveEditingStatus } from './types'
import { BehaviorSubject, Subject } from 'rxjs'
import { apiEndpoint } from '@awork/environments/environment'
import type { RealtimeChannelDiagnostics } from './realtime-channel-diagnostics'

@Injectable({ providedIn: 'root' })
export class LiveEditingService {
  readonly needsContentInit$ = new Subject<LiveEditingContentInitEvent>()
  readonly needsContentReset$ = new Subject<LiveEditingContentInitEvent>()
  readonly status$ = new Subject<LiveEditingStatus>()
  readonly documentStatus$ = new Subject<LiveEditingStatusEvent>()
  private activeProviderCountSubject = new BehaviorSubject<number>(0)
  private lastHeartbeatAtSubject = new BehaviorSubject<number | null>(null)
  private currentStatus: LiveEditingStatus = LiveEditingStatus.Disconnected
  private reconnectCount = 0
  private lastError: string | null = null

  readonly activeProviderCount$ = this.activeProviderCountSubject.asObservable()
  readonly lastHeartbeatAt$ = this.lastHeartbeatAtSubject.asObservable()

  /**
   * Initializes the live-editing lifecycle state for a provider instance.
   */
  init(): void {
    this.incrementActiveProviderCount()
    this.markHeartbeat()
  }

  /**
   * Handles destroying a provider instance.
   */
  onDestroy(): void {
    this.decrementActiveProviderCount()
  }

  /**
   * Marks a successful live-editing heartbeat.
   */
  markHeartbeat(): void {
    this.lastHeartbeatAtSubject.next(Date.now())
  }

  /**
   * Updates and emits the current live-editing connection status.
   * @param {LiveEditingStatus} status The new provider status.
   * @param {string} documentName The provider's document room name.
   */
  setStatus(status: LiveEditingStatus, documentName: string): void {
    this.currentStatus = status
    this.status$.next(status)
    this.documentStatus$.next({ documentName, status })
  }

  /**
   * Marks a successful live-editing reconnection.
   */
  markReconnected(): void {
    this.reconnectCount++
    this.lastError = null
  }

  /**
   * Tracks a live-editing connection error for diagnostics.
   * @param {string} message
   */
  markConnectionError(message: string): void {
    this.lastError = message
  }

  /**
   * Returns a live-editing channel diagnostics snapshot.
   * @param {number} nowMs
   * @returns {RealtimeChannelDiagnostics | null}
   */
  getRealtimeDiagnosticsSnapshot(nowMs: number): RealtimeChannelDiagnostics | null {
    const heartbeatTimestamp = this.lastHeartbeatAtSubject.value
    const activeProviders = this.activeProviderCountSubject.value
    const hubUrl = new URL(`${apiEndpoint}/liveediting/signalr`, window.location.origin)

    return {
      state: activeProviders > 0 ? (this.currentStatus as RealtimeChannelDiagnostics['state']) : 'inactive',
      host: hubUrl.host,
      hubPath: hubUrl.pathname,
      connectionId: null,
      reconnectCount: this.reconnectCount,
      lastError: this.lastError,
      lastHeartbeatAt: heartbeatTimestamp ? new Date(heartbeatTimestamp).toISOString() : null,
      heartbeatAgeMs: heartbeatTimestamp ? nowMs - heartbeatTimestamp : null
    }
  }

  /**
   * Increases the number of active live-editing providers.
   */
  private incrementActiveProviderCount(): void {
    this.activeProviderCountSubject.next(this.activeProviderCountSubject.value + 1)
  }

  /**
   * Decreases the number of active live-editing providers.
   * Resets heartbeat state when no providers remain.
   */
  private decrementActiveProviderCount(): void {
    const nextCount = Math.max(0, this.activeProviderCountSubject.value - 1)

    this.activeProviderCountSubject.next(nextCount)

    if (nextCount === 0) {
      this.lastHeartbeatAtSubject.next(null)
      this.currentStatus = LiveEditingStatus.Disconnected
    }
  }
}
